<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="filterParts"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            :height="$vuetify.breakpoint.mdAndDown ? '59vh' : '54vh'"
            fixed-header
            :footer-props="{
                itemsPerPageOptions: [15, 30, 100, -1],
                itemsPerPageText: 'Part Numbers Per Page',
            }"
        >
            <template v-slot:top>
                <div
                    class="d-flex flex-row flex-wrap px-3 pt-2"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col>
                        <v-row>
                            <div
                                v-for="(insight, index) in insights"
                                :key="index"
                                class="d-flex justify-in-line"
                            >
                                <v-card
                                    class="pt-2 px-2 mr-3 my-1"
                                    min-width="250px"
                                    max-width="250px"
                                >
                                    <v-card-text
                                        class="d-flex my-0 py-0 mx-0 px-0"
                                        width="100%"
                                    >
                                        <v-chip
                                            class="mb-0 mt-0 mx-1 pt-0 white--text"
                                            :color="insight.color"
                                            label
                                            :style="{ height: '45px' }"
                                        >
                                            <v-icon center>
                                                {{ insight.icon }}
                                            </v-icon>
                                        </v-chip>
                                        <div class="mb-0 mt-0 pt-0">
                                            <div
                                                class="d-flex mr-0 pr-0 text-capitalize"
                                            >
                                                <span
                                                    :style="{
                                                        'font-size': '13px',
                                                        'font-weight': 'bold',
                                                    }"
                                                    >{{ insight.title }}</span
                                                >
                                            </div>
                                            <p
                                                class="text-h6 black--text mb-0"
                                                style="font-size: 18px !important;"
                                            >
                                                {{
                                                    insight.isPercentage
                                                        ? progress[
                                                              insight.value
                                                          ].toFixed(2) + '%'
                                                        : progress[
                                                              insight.value
                                                          ]
                                                }}
                                            </p>
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </div>
                            <v-spacer />
                            <v-btn
                                rounded
                                color="primary"
                                @click="triggerFileUpload"
                                class="mx-1"
                                v-if="createPermission"
                            >
                                <v-icon>
                                    mdi-file-excel
                                </v-icon>
                                {{
                                    $vuetify.breakpoint.smAndDown
                                        ? ''
                                        : 'UPLOAD EXCEL'
                                }}
                            </v-btn>
                            <input
                                ref="fileInput"
                                type="file"
                                accept=".xlsx,.xls"
                                style="display: none"
                                @change="onFileSelected"
                            />
                            <v-btn
                                rounded
                                color="primary"
                                @click="openDialogCreate"
                                class="mx-1"
                                v-if="createPermission"
                            >
                                <v-icon>
                                    mdi-plus
                                </v-icon>
                                {{ $vuetify.breakpoint.smAndDown ? '' : 'NEW' }}
                            </v-btn>
                        </v-row>
                    </v-col>
                </div>
            </template>
            <!-- headers -->
            <template v-slot:[`header.createdOn`]="{ header }">
                <div class="d-flex">
                    <p class="ma-0">{{ header.text }}</p>
                    <v-btn
                        icon
                        class="mt-n2 text-uppercase"
                        @click="sortCreatedOn = !sortCreatedOn"
                    >
                        <v-icon v-if="sortCreatedOn">mdi-arrow-up</v-icon>
                        <v-icon v-else>mdi-arrow-down</v-icon>
                    </v-btn>
                </div>
            </template>
            <template v-slot:[`header.code`]="{ header }">
                <v-text-field :label="header.text" v-model="codeToFilter" />
            </template>

            <template v-slot:[`header.product`]="{ header }">
                <v-text-field :label="header.text" v-model="productToFilter" />
            </template>

            <template v-slot:[`header.material`]="{ header }">
                <v-text-field :label="header.text" v-model="materialToFilter" />
            </template>

            <template v-slot:[`header.woStatusQty`]="{ header }">
                <v-autocomplete
                    :label="header.text"
                    v-model="woStatusToFilter"
                    :items="[
                        'inProgress',
                        'sent',
                        'rejected',
                        'approved',
                        'finished',
                        'closed',
                    ]"
                    multiple
                    small-chips
                    deletable-chips
                />
            </template>
            <template v-slot:[`header.prodQty`]="{ header }">
                <p class="ma-0 pa-0">{{ header.text }}</p>
            </template>
            <template v-slot:[`header.pkgStatusQty`]="{ header }">
                <v-autocomplete
                    :label="header.text"
                    v-model="pkgStatusQtyToFilter"
                    :items="[
                        'inProgress',
                        'rejected',
                        'pending',
                        'scheduled',
                        'referred',
                        'shipped',
                        'completed',
                    ]"
                    multiple
                    small-chips
                    deletable-chips
                />
            </template>
            <template v-slot:[`header.workOrderDetails`]="{ header }">
                <div class="d-flex align-center">
                    <p class="ma-0">{{ header.text }}</p>
                    <v-tooltip bottom max-width="280">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                                mdi-information-outline
                            </v-icon>
                        </template>
                        <span>
                            Format: CODE (order qty)
                        </span>
                    </v-tooltip>
                </div>
            </template>
            <template v-slot:[`header.usedIn`]="{ header }">
                <div class="d-flex align-center">
                    <p class="ma-0">{{ header.text }}</p>
                    <v-tooltip bottom max-width="280">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                                mdi-information-outline
                            </v-icon>
                        </template>
                        <span>
                            Format: CODE (required qty)
                        </span>
                    </v-tooltip>
                </div>
            </template>
            <!--Items--->
            <template v-slot:[`item.createdOn`]="{ item }">
                <div class="d-flex justify-left">
                    <p class="px-0 py-0 my-0">
                        {{ formatDate(item.createdOn) }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.available`]="{ item }">
                <div class="text-right">
                    <p class="pa-0 ma-0">
                        {{ item.available || 0 }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.totalQty`]="{ item }">
                <div class="text-right">
                    <p class="pa-0 ma-0">
                        {{ item.totalQty || 0 }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.producedQty`]="{ item }">
                <div class="text-right">
                    <p class="pa-0 ma-0">
                        {{ item.producedQty || 0 }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.pendingToProduce`]="{ item }">
                <div class="text-right">
                    <p class="pa-0 ma-0">
                        {{ (item.totalQty || 0) - (item.producedQty || 0) }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.pending`]="{ item }">
                <div class="text-right">
                    <p class="pa-0 ma-0">
                        {{
                            (item.totalQty || 0) - (item.packing?.shipped || 0)
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.createdBy`]="{ item }">
                <div class="d-flex justify-left px-0">
                    <p class="px-0 py-0 my-0">
                        {{ item.createdByName }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.code`]="{ item }">
                <div class="d-flex justify-left">
                    <p class="px-0 py-0 my-0 text-capitalize">
                        {{ item.code }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.product`]="{ item }">
                <div class="d-flex justify-left">
                    <p class="px-0 py-0 my-0">
                        {{ item.product }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.workOrderDetails`]="{ item }">
                <div class="d-flex flex-column justify-left">
                    <p
                        class="px-0 py-0 my-0"
                        v-for="wo in item.workOrderDetails || []"
                        :key="wo.code"
                    >
                        {{ wo.code }} ({{ Math.max(0, wo.quantity || 0) }})
                    </p>
                </div>
            </template>
            <template v-slot:[`item.usedIn`]="{ item }">
                <div class="d-flex flex-column justify-left">
                    <p
                        class="px-0 py-0 my-0"
                        v-for="wo in item.usedIn || []"
                        :key="wo.code"
                    >
                        {{ wo.code }} ({{ Math.max(0, wo.requiredQty || 0) }})
                    </p>
                </div>
            </template>
            <template v-slot:[`item.material`]="{ item }">
                <div class="d-flex">
                    <p class="px-0 py-0 my-0">
                        {{ item.material }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.woStatusQty`]="{ item }">
                <div class="text-right">
                    <p class="px-0 py-0 my-0">
                        {{ calculateWoStatusQty(item) }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.pkgStatusQty`]="{ item }">
                <div class="text-right">
                    <p class="px-0 py-0 my-0">
                        {{ calculatePckStatusQty(item) }}
                    </p>
                </div>
            </template>
            <template
                v-if="this.deletePermission"
                v-slot:[`item.actions`]="{ item }"
            >
                <div class="d-flex justify-center">
                    <v-btn small icon @click="openDialogDelete(item)">
                        <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>

        <!--Part Number Delete-->
        <v-dialog
            :retain-focus="false"
            v-model="openPartNumberDelete"
            persistent
            width="40%"
        >
            <PartNumberDelete
                v-if="openPartNumberDelete"
                :item="selectedItem"
                @closeDialog="closeDialogDelete"
                @deletePartNumber="deletePartNumber"
                :projectId="this.projectId"
            />
        </v-dialog>

        <!--Part Number Create-->
        <v-dialog
            :retain-focus="false"
            v-model="openPartNumberCreate"
            persistent
            width="40%"
        >
            <PartNumberCreate
                v-if="openPartNumberCreate"
                @closeDialog="closeDialogCreate"
                @createPartNumber="createPartNumber"
                :projectId="this.projectId"
            />
        </v-dialog>

        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{ position: 'absolute', right: '0px', bottom: '0px' }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
    </div>
</template>

<script>
import API from '@/services/api'
import _ from 'lodash'
import { mapMutations } from 'vuex'
import moment from 'moment'
import PartNumberDelete from '@/components/StockRoom/PartNumberDelete.vue'
import PartNumberCreate from '@/components/StockRoom/PartNumberCreate.vue'
import { firestore } from '../../services/firebase'
import * as XLSX from 'xlsx'

export default {
    name: 'PartNumbers',
    props: {
        projectId: {
            type: String,
            default: () => undefined,
        },
    },
    components: {
        PartNumberDelete,
        PartNumberCreate,
    },
    data: () => ({
        activateArrow: true,
        activateAlert: false,
        openPartNumberDelete: false,
        openPartNumberCreate: false,
        partNumbersInfo: null,
        loading: false,
        sortCreatedOn: false,
        selectedItem: {},
        createdBy: null,
        progress: {
            totalPartNumbers: 0,
            productionProgress: 0,
            designProgress: 0,
            shippingProgress: 0,
        },
        insights: [
            {
                color: '#3498DB',
                icon: 'mdi-package-variant',
                iconColor: '#1A5276',
                title: 'PART NUMBERS',
                value: 'totalPartNumbers',
                isPercentage: false,
            },
            {
                color: '#ffa414',
                icon: 'mdi-factory',
                iconColor: '#1A5276',
                title: 'PRODUCTION PROGRESS',
                value: 'productionProgress',
                isPercentage: true,
            },
            {
                color: '#3498DB',
                icon: 'mdi-cube-outline',
                iconColor: '#2874A6',
                title: 'DESIGN PROGRESS',
                value: 'designProgress',
                isPercentage: true,
            },
            {
                color: '#20844c',
                icon: 'mdi-truck',
                iconColor: '#1A5276',
                title: 'SHIPPING PROGRESS',
                value: 'shippingProgress',
                isPercentage: true,
            },
        ],
        headers: [
            {
                text: 'CREATED ON',
                value: 'createdOn',
                align: 'left',
                sortable: false,
                width: 125,
            },
            {
                text: 'CREATED BY',
                value: 'createdBy',
                align: 'left',
                sortable: false,
                width: 120,
            },
            {
                text: 'CODE',
                value: 'code',
                align: 'left',
                sortable: false,
                width: 140,
            },
            {
                text: 'PRODUCT',
                value: 'product',
                align: 'left',
                sortable: false,
                width: 135,
            },
            {
                text: 'MATERIAL',
                value: 'material',
                align: 'center',
                sortable: false,
                width: 150,
            },
            {
                text: 'WORK ORDERS',
                value: 'workOrderDetails',
                align: 'left',
                sortable: false,
                width: 120,
            },
            {
                text: 'USED IN',
                value: 'usedIn',
                align: 'left',
                sortable: false,
                width: 110,
            },

            {
                text: 'TOTAL QTY',
                value: 'totalQty',
                align: 'right',
                sortable: true,
                width: 70,
            },
            {
                text: 'AVAILABLE',
                value: 'available',
                align: 'right',
                sortable: true,
                width: 80,
            },
            {
                text: 'PRODUCED QTY',
                value: 'producedQty',
                align: 'right',
                sortable: true,
                width: 80,
            },
            {
                text: 'PENDING TO PRODUCE',
                value: 'pendingToProduce',
                align: 'center',
                sortable: true,
                width: 100,
            },
            {
                text: 'WO STATUS QTY',
                value: 'woStatusQty',
                align: 'center',
                sortable: false,
                width: 130,
            },
            {
                text: 'BACK ORDER',
                value: 'pending',
                align: 'center',
                sortable: false,
                width: 70,
            },
            {
                text: 'PACKING STATUS QTY',
                value: 'pkgStatusQty',
                align: 'center',
                sortable: false,
                width: 165,
            },
            {
                text: 'NOTES',
                value: 'notes',
                align: 'center',
                sortable: true,
                width: 110,
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                align: 'center',
                sortable: false,
            },
        ],
        partNumbers: [],
        users: [],
        user: undefined,
        search: '',
        alertMessage: '',
        originalItem: {},
        approveStock: undefined,
        codeToFilter: undefined,
        productToFilter: undefined,
        materialToFilter: undefined,
        woStatusToFilter: [],
        pkgStatusQtyToFilter: [],
        deletePermission: false,
        createPermission: false,
        partNumbersProductionData: {},
        partNumbersUnsubscribe: null,
        partNumberIndex: {},
    }),
    computed: {
        filterParts() {
            let conditions = []
            const items = this.sortByCreatedOn(this.partNumbers)

            if (this.codeToFilter) {
                conditions.push(this.filterForCode)
            }

            if (this.productToFilter) {
                conditions.push(this.filterForProduct)
            }

            if (this.materialToFilter) {
                conditions.push(this.filterForMaterial)
            }

            if (this.woStatusToFilter.length > 0) {
                conditions.push(this.filterForWoStatus)
            }

            if (this.pkgStatusQtyToFilter.length > 0) {
                conditions.push(this.filterForPkgStatusQty)
            }

            if (conditions.length > 0) {
                return items.filter(partNumber => {
                    return conditions.every(condition => {
                        return condition(partNumber)
                    })
                })
            }
            return items
        },
    },
    watch: {
        filterParts: {
            handler() {
                this.calculateMetrics()
            },
            immediate: true,
        },
    },
    async mounted() {
        try {
            this.loading = true
            const {
                data: { user },
            } = await API.getMyInfo()
            this.user = user
            const {
                data: { users },
            } = await API.getUsers()
            this.users = users
            this.createPermission = user.permissions.includes(
                'createPartNumbers'
            )
            this.deletePermission = user.permissions.includes(
                'deletePartNumbers'
            )
            // get partnumbers data
            this.partNumbersProductionData = await API.getPartNumbersProductionData(
                this.projectId
            )
            await this.getPartNumbers()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        if (this.partNumbersUnsubscribe) {
            this.partNumbersUnsubscribe()
            this.partNumbersUnsubscribe = null
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        filterForCode(item) {
            return (
                item.code &&
                item.code
                    .toLowerCase()
                    .includes(this.codeToFilter.toLowerCase())
            )
        },
        filterForProduct(item) {
            return (
                item.product &&
                item.product
                    .toLowerCase()
                    .includes(this.productToFilter.toLowerCase())
            )
        },
        filterForMaterial(item) {
            return (
                item.material &&
                item.material
                    .toLowerCase()
                    .includes(this.materialToFilter.toLowerCase())
            )
        },
        filterForWoStatus(item) {
            if (item.wo) {
                let total = 0
                this.woStatusToFilter.forEach(status => {
                    total += Number(item.wo[status] || 0)
                })
                return total > 0
            } else return false
        },
        filterForPkgStatusQty(item) {
            if (item.packing) {
                let total = 0
                this.pkgStatusQtyToFilter.forEach(status => {
                    total += Number(item.packing[status] || 0)
                })
                return total > 0
            } else return false
        },
        formatDate(date) {
            return moment
                .unix(date._seconds || date.seconds)
                .format('YYYY-MM-DD')
        },
        getPartNumberCodeSuffix(code) {
            if (!code) return 0
            const num = parseInt(code.slice(-4), 10)
            return Number.isNaN(num) ? 0 : num
        },
        comparePartNumberCodes(a, b) {
            const suffixDiff =
                this.getPartNumberCodeSuffix(a?.code) -
                this.getPartNumberCodeSuffix(b?.code)
            if (suffixDiff !== 0) return suffixDiff
            return (a?.code || '').localeCompare(b?.code || '')
        },
        sortByCreatedOn(items) {
            const sorted = items.slice()
            if (this.sortCreatedOn) {
                sorted.sort((a, b) => this.comparePartNumberCodes(a, b))
            } else {
                sorted.sort((a, b) => this.comparePartNumberCodes(b, a))
            }
            return sorted
        },
        getPartNumbers() {
            return new Promise((resolve, reject) => {
                try {
                    if (this.partNumbersUnsubscribe) {
                        this.partNumbersUnsubscribe()
                        this.partNumbersUnsubscribe = null
                    }

                    const collectionRef = firestore
                        .collection('companies')
                        .doc(this.user.companyId)
                        .collection('projects')
                        .doc(this.projectId)
                        .collection('partNumbers')

                    let initialLoad = true

                    this.partNumbersUnsubscribe = collectionRef.onSnapshot(
                        querySnapshot => {
                            if (this._isDestroyed) {
                                return
                            }
                            this.applyPartNumberSnapshot(querySnapshot)
                            if (initialLoad) {
                                initialLoad = false
                                resolve()
                            }
                        },
                        error => {
                            this.setErrorItems({
                                source: this.$options.name,
                                message: error.message,
                            })
                            if (initialLoad) {
                                initialLoad = false
                                reject(error)
                            }
                        }
                    )
                } catch (error) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: error.message,
                    })
                    reject(error)
                }
            })
        },
        applyPartNumberSnapshot(querySnapshot) {
            const changes = querySnapshot.docChanges()
            if (!changes.length) {
                return
            }

            const isInitialLoad =
                this.partNumbers.length === 0 &&
                changes.every(change => change.type === 'added')

            if (isInitialLoad) {
                const rows = new Array(changes.length)
                const indexMap = Object.create(null)
                for (let i = 0; i < changes.length; i++) {
                    const row = this.buildPartNumberRow(changes[i].doc)
                    rows[i] = row
                    indexMap[row.id] = i
                }
                this.partNumbers = rows
                this.partNumberIndex = indexMap
                return
            }

            changes.forEach(change => {
                const id = change.doc.id
                if (change.type === 'removed') {
                    const index = this.partNumberIndex[id]
                    if (index !== undefined) {
                        this.partNumbers.splice(index, 1)
                        this.rebuildPartNumberIndex()
                    }
                    return
                }

                const row = this.buildPartNumberRow(change.doc)
                const index = this.partNumberIndex[id]
                if (index !== undefined) {
                    this.$set(this.partNumbers, index, row)
                } else {
                    this.partNumberIndex[id] = this.partNumbers.length
                    this.partNumbers.push(row)
                }
            })
        },
        buildPartNumberRow(doc) {
            const productionData = this.partNumbersProductionData[doc.id] || {}
            const row = {
                id: doc.id,
                ...doc.data(),
                ...productionData,
            }
            if (!row.workOrderDetails && row.workOrderCodes) {
                row.workOrderDetails = row.workOrderCodes.map(code => ({
                    code,
                    quantity: 0,
                }))
            }
            row.createdByName = this.getUserName(row.createdBy)
            return row
        },
        rebuildPartNumberIndex() {
            const indexMap = Object.create(null)
            for (let i = 0; i < this.partNumbers.length; i++) {
                indexMap[this.partNumbers[i].id] = i
            }
            this.partNumberIndex = indexMap
        },
        getUserName(id) {
            if (this.users.length > 0) {
                return this.users.find(user => user.id == id)?.name
            } else {
                return ''
            }
        },
        calculateMetrics() {
            // Calculate metrics for the cards
            let totalPartNumbers = this.filterParts.length
            let totalQuantity = 0
            let totalProduced = 0
            let totalShipped = 0
            let totalDesigned = 0

            this.filterParts.forEach(partNumber => {
                totalQuantity += +partNumber.totalQty || 0
                totalProduced += +partNumber.producedQty || 0
                totalShipped += +partNumber.packing?.shipped || 0
                if (partNumber.wo && Object.values(partNumber.wo).length > 0) {
                    Object.values(partNumber.wo).forEach(status => {
                        if (status != 'finished' && status != 'cancelled') {
                            totalDesigned += +status || 0
                        }
                    })
                }
            })

            // Update progress with calculated values
            this.progress.totalPartNumbers = totalPartNumbers
            // production progress percentage
            this.progress.productionProgress =
                totalQuantity > 0 ? (totalProduced / totalQuantity) * 100 : 0
            // shipping progress percentage
            this.progress.shippingProgress =
                totalQuantity > 0 ? (totalShipped / totalQuantity) * 100 : 0
            // fabrication progress percentage
            this.progress.designProgress =
                totalQuantity > 0 ? (totalDesigned / totalQuantity) * 100 : 0
        },
        deletePartNumber(code) {
            const index = this.partNumbers.findIndex(item => item.code == code)
            if (index > -1) {
                this.partNumbers.splice(index, 1)
                this.rebuildPartNumberIndex()
            }
            this.closeDialogDelete()
            this.alertMessage = 'Part Number was successfully removed'
            this.activateAlert = true
        },
        createPartNumber() {
            this.closeDialogCreate()
            this.alertMessage = 'Part Number was successfully created'
            this.activateAlert = true
        },
        openDialogDelete(item) {
            this.openPartNumberDelete = true
            this.selectedItem = _.cloneDeep(item)
        },
        closeDialogDelete() {
            this.openPartNumberDelete = false
            this.selectedItem = {}
        },
        openDialogCreate() {
            this.openPartNumberCreate = true
        },
        closeDialogCreate() {
            this.openPartNumberCreate = false
        },
        calculateWoStatusQty(item) {
            if (item.wo) {
                let total = 0
                this.woStatusToFilter.forEach(status => {
                    total += Number(item.wo[status] || 0)
                })
                return total
            } else return 0
        },
        calculatePckStatusQty(item) {
            if (item.packing) {
                let total = 0
                this.pkgStatusQtyToFilter.forEach(status => {
                    total += Number(item.packing[status] || 0)
                })
                return total
            } else return 0
        },
        sentToStock(item, code) {
            const total = (item.deliveries || [])
                .filter(
                    d => d.originWorkOrder === code && !d.destinationWorkOrder
                )
                .reduce((acc, d) => acc + (+d.qty || 0), 0)
            return Math.max(0, total)
        },
        availableInStock(item, code) {
            const total = (item.deliveries || [])
                .filter(d => d.originWorkOrder === code)
                .reduce((acc, d) => acc + (+d.qty || 0), 0)
            return Math.max(0, total)
        },
        triggerFileUpload() {
            this.$refs.fileInput.click()
        },
        onFileSelected(event) {
            const file = event.target.files[0]
            if (file) {
                this.processExcelFile(file)
            }
            event.target.value = ''
        },
        async processExcelFile(file) {
            try {
                // validate file
                if (!file) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: 'Please select an Excel file',
                    })
                    return
                }
                // Read the Excel file
                const workbook = XLSX.read(
                    await this.readFileAsArrayBuffer(file),
                    {
                        type: 'array',
                    }
                )

                // Get the first worksheet
                const sheetName = workbook.SheetNames[0]
                const worksheet = workbook.Sheets[sheetName]

                // Convert to JSON
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                })

                if (jsonData.length < 2) {
                    throw new Error(
                        'Excel file must have at least a header row and one data row'
                    )
                }

                // Get headers and normalize them
                const headers = jsonData[0].map(header =>
                    header
                        ? header
                              .toString()
                              .toLowerCase()
                              .trim()
                        : ''
                )

                // Find required columns
                const columnIndexes = this.findColumnIndexes(headers)

                // Process data rows
                const updates = this.extractUpdatesFromData(
                    jsonData,
                    columnIndexes
                )

                if (updates.length === 0) {
                    throw new Error('No valid data found in Excel file')
                }

                // Update part numbers
                const response = await this.updatePartNumbers(updates)

                this.alertMessage = `Successfully updated ${response.length} part number(s)`
                this.activateAlert = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message || 'Error processing Excel file',
                })
            } finally {
                this.loading = false
            }
        },
        findColumnIndexes(headers) {
            const partNumbersIndex = headers.findIndex(header => {
                const cleanHeader = header.replace(/\s+/g, '')
                return (
                    cleanHeader.includes('partnumber') ||
                    header.includes('part number') ||
                    header.includes('partnumbers') ||
                    header.includes('part numbers')
                )
            })

            const totalQtyIndex = headers.findIndex(header => {
                const cleanHeader = header.replace(/\s+/g, '')
                return (
                    header.includes('total quantity') ||
                    header.includes('total qty') ||
                    cleanHeader.includes('totalqty') ||
                    cleanHeader.includes('totalquantity')
                )
            })

            const notesIndex = headers.findIndex(
                header => header.includes('note') || header.includes('notes')
            )

            // Validate required columns
            if (partNumbersIndex === -1) {
                throw new Error('Required column "partnumbers" not found')
            }
            if (totalQtyIndex === -1) {
                throw new Error('Required column "total quantity" not found')
            }

            return {
                partNumbers: partNumbersIndex,
                totalQty: totalQtyIndex,
                notes: notesIndex,
            }
        },
        extractUpdatesFromData(jsonData, columnIndexes) {
            const updates = []
            const dataRows = jsonData.slice(1) // Skip header row

            for (const row of dataRows) {
                // Skip empty rows
                if (!row || row.length === 0) continue

                // Extract data from each column
                const partNumber =
                    row[columnIndexes.partNumbers]?.toString().trim() || ''
                const totalQty = row[columnIndexes.totalQty]
                    ? Number(row[columnIndexes.totalQty])
                    : 0
                const notes =
                    columnIndexes.notes !== -1
                        ? row[columnIndexes.notes]?.toString().trim() || ''
                        : ''
                // Validate and add to updates
                if (partNumber && totalQty !== null && !isNaN(totalQty)) {
                    updates.push({
                        code: partNumber,
                        totalQty: totalQty,
                        notes: notes,
                    })
                }
            }
            return updates
        },
        readFileAsArrayBuffer(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = e => resolve(e.target.result)
                reader.onerror = e => reject(e)
                reader.readAsArrayBuffer(file)
            })
        },
        async updatePartNumbers(updates) {
            try {
                this.loading = true

                const response = await API.updatePartNumbersByXLSX({
                    projectId: this.projectId,
                    updates: updates,
                })

                return response
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
                throw error
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style>
.v-data-table {
    border: 1px solid #eeeeee;
}

thead {
    background: #eeeeee;
}

.v-input .v-label {
    font-size: 12px;
}

.v-data-table-header th {
    background-color: #eeeeee !important;
}

.v-data-table td {
    padding: 0px 8px !important;
}

.v-data-table th {
    padding: 0px 8px !important;
}
</style>
